import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Shield, Loader2, CheckCircle2, AlertCircle, BellOff } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const API = 'https://api.threat.live/client/unsubscribe';

const UnsubscribePage = () => {
  const { language } = useLanguage();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [status, setStatus] = useState('loading'); // loading, ready, already, success, error
  const [asn, setAsn] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const translations = {
    tr: {
      loading: 'Bilgiler yükleniyor...',
      title: 'E-posta bildirimlerinden çık',
      desc: (a, e) => `${a} için gönderdiğimiz tehdit bildirim e-postalarını durdurmak üzeresiniz (${e}).`,
      button: 'Bildirimleri durdur',
      already: 'Abonelik zaten durdurulmuş',
      alreadyDesc: (a) => `${a} için bildirim e-postaları zaten gönderilmiyor.`,
      success: 'Abonelik durduruldu',
      successDesc: (a) => `${a} için artık bildirim e-postası göndermeyeceğiz.`,
      error: 'Geçersiz veya süresi dolmuş bağlantı.',
      home: 'Ana sayfaya dön',
    },
    en: {
      loading: 'Loading...',
      title: 'Unsubscribe from notifications',
      desc: (a, e) => `You are about to stop the threat notification emails we send for ${a} (${e}).`,
      button: 'Stop notifications',
      already: 'Already unsubscribed',
      alreadyDesc: (a) => `Notification emails for ${a} are already stopped.`,
      success: 'Unsubscribed',
      successDesc: (a) => `We will no longer send notification emails for ${a}.`,
      error: 'Invalid or expired link.',
      home: 'Back to home',
    },
  };
  const txt = translations[language] || translations.en;

  useEffect(() => {
    let called = false;
    const load = async () => {
      if (called) return;
      called = true;
      if (!token) {
        setStatus('error');
        return;
      }
      try {
        const res = await fetch(`${API}/info.php?token=${encodeURIComponent(token)}`, {
          method: 'GET',
          credentials: 'include',
        });
        const data = await res.json();
        if (data.status === 'ok') {
          setAsn(data.asn || '');
          setEmail(data.email || '');
          setStatus(data.unsubscribed ? 'already' : 'ready');
        } else {
          setStatus('error');
        }
      } catch (e) {
        setStatus('error');
      }
    };
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const doUnsubscribe = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/confirm.php`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      const data = await res.json();
      setStatus(data.status === 'ok' ? 'success' : 'error');
    } catch (e) {
      setStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="unsubscribe-page">
      <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 via-transparent to-cyan-500/5"></div>
      <div className="absolute top-20 left-10 w-72 h-72 bg-emerald-500/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-10 right-10 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl"></div>

      <div className="relative z-10 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-lg blur opacity-75"></div>
              <div className="relative bg-gradient-to-r from-emerald-600 to-cyan-600 p-3 rounded-lg">
                <Shield className="h-8 w-8 text-white" />
              </div>
            </div>
            <span className="text-2xl font-bold tracking-tight bg-gradient-to-r from-emerald-500 to-cyan-500 bg-clip-text text-transparent">
              Threat.live
            </span>
          </div>
        </div>

        <Card className="border-0 bg-white dark:bg-zinc-900 shadow-2xl">
          <CardContent className="pt-8 pb-8">
            {status === 'loading' && (
              <div className="text-center">
                <Loader2 className="h-12 w-12 animate-spin text-emerald-500 mx-auto mb-4" />
                <p className="text-lg font-medium">{txt.loading}</p>
              </div>
            )}

            {status === 'ready' && (
              <div className="text-center">
                <div className="mx-auto w-16 h-16 rounded-full bg-amber-500/10 flex items-center justify-center mb-4">
                  <BellOff className="h-8 w-8 text-amber-500" />
                </div>
                <h3 className="text-xl font-semibold mb-2">{txt.title}</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  {txt.desc(asn, email)}
                </p>
                <Button
                  onClick={doUnsubscribe}
                  disabled={submitting}
                  className="w-full bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 text-white"
                  data-testid="unsubscribe-confirm-btn"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  {txt.button}
                </Button>
              </div>
            )}

            {status === 'already' && (
              <div className="text-center">
                <div className="mx-auto w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mb-4">
                  <CheckCircle2 className="h-8 w-8 text-emerald-500" />
                </div>
                <h3 className="text-xl font-semibold mb-2 text-emerald-600">{txt.already}</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">{txt.alreadyDesc(asn)}</p>
                <a href="/" className="text-emerald-600 hover:underline">{txt.home}</a>
              </div>
            )}

            {status === 'success' && (
              <div className="text-center">
                <div className="mx-auto w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mb-4">
                  <CheckCircle2 className="h-8 w-8 text-emerald-500" />
                </div>
                <h3 className="text-xl font-semibold mb-2 text-emerald-600">{txt.success}</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">{txt.successDesc(asn)}</p>
                <a href="/" className="text-emerald-600 hover:underline">{txt.home}</a>
              </div>
            )}

            {status === 'error' && (
              <div className="text-center">
                <div className="mx-auto w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mb-4">
                  <AlertCircle className="h-8 w-8 text-red-500" />
                </div>
                <h3 className="text-xl font-semibold mb-2 text-red-600">{txt.error}</h3>
                <a href="/" className="text-emerald-600 hover:underline">{txt.home}</a>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default UnsubscribePage;
